import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, Modal, PageHeader, Spinner, statusTone, Table } from "../components/ui";
import { STATUS_LABELS, fmtDateTime, fmtPct } from "../lib/format";

type Monitor = {
  id: string; testId: string; name: string; sender: string; frequency: string; nextRunAt: string | null;
  lastRunAt: string | null; inboxPct: number | null; spamPct: number | null; status: string;
};

export function Monitoring() {
  const qc = useQueryClient();
  const [history, setHistory] = useState<any>(null);
  const { data, isLoading } = useQuery({ queryKey: ["monitoring"], queryFn: () => api<Monitor[]>("/monitoring") });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "pause" | "resume" | "run-now" }) => api(`/monitoring/${id}/${action}`, { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitoring"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/monitoring/${id}`, { method: "DELETE", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitoring"] }),
  });

  return (
    <div>
      <PageHeader title="Automated Monitoring" subtitle="Recurring tests that continuously track sender performance" />
      <Card className="p-0">
        {isLoading ? <Spinner /> : !data?.length ? (
          <div className="p-6"><EmptyState title="No automated monitors" description="Create an automated test in the wizard to start monitoring sender performance over time." action={<Link to="/tests/new"><Button>Create automated test</Button></Link>} /></div>
        ) : (
          <Table headers={["Test", "Sender", "Frequency", "Next Run", "Last Run", "Inbox %", "Spam %", "Status", "Actions"]}>
            {data!.map((m) => (
              <tr key={m.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">{m.name}</td>
                <td className="px-4 py-3 text-ink-600">{m.sender}</td>
                <td className="px-4 py-3 capitalize text-ink-600">{m.frequency}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(m.nextRunAt)}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(m.lastRunAt)}</td>
                <td className="px-4 py-3 font-medium text-emerald-600">{fmtPct(m.inboxPct)}</td>
                <td className="px-4 py-3 font-medium text-red-600">{fmtPct(m.spamPct)}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(m.status)}>{STATUS_LABELS[m.status]}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {m.status === "active"
                      ? <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => act.mutate({ id: m.id, action: "pause" })}>Pause</button>
                      : <button className="rounded px-2 py-1 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => act.mutate({ id: m.id, action: "resume" })}>Resume</button>}
                    <button className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50" onClick={() => act.mutate({ id: m.id, action: "run-now" })}>Run Now</button>
                    <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={async () => {
                      const res = await api(`/monitoring/${m.id}/history`);
                      setHistory(res);
                    }}>History</button>
                    <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete monitor ${m.name}?`)) del.mutate(m.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={Boolean(history)} onClose={() => setHistory(null)} title={history ? `Run history — ${history.schedule.testId}` : ""} wide>
        {history && (
          <Table headers={["Run", "Status", "Inbox %", "Spam %", "Missing", "Started", "Completed"]}>
            {history.runs.map((r: any) => (
              <tr key={r.id} className="table-row-hover">
                <td className="px-4 py-3 font-medium text-ink-800">#{r.runNumber}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(r.status)}>{STATUS_LABELS[r.status]}</Badge></td>
                <td className="px-4 py-3 text-emerald-600">{fmtPct(r.summary?.inboxPct)}</td>
                <td className="px-4 py-3 text-red-600">{fmtPct(r.summary?.spamPct)}</td>
                <td className="px-4 py-3 tabular-nums">{r.summary?.missing ?? "—"}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(r.createdAt)}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(r.completedAt)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Modal>
    </div>
  );
}
